import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import {
  appendToFolderAtPath,
  appendToRoot,
  type DropPosition,
  getAtPath,
  insertAtPath,
  isDescendantPath,
  moveNode,
  removeAtPath,
  shiftPathAfterRemoval,
  updateAtPath,
} from './skills-path';

/**
 * High-level pure operations on the skills tree. Each takes the current
 * `skills` (and other inputs) and returns the next `skills` plus an optional
 * `selectedPath` patch. Callers in the actions hook decide whether to apply
 * the selection patch.
 *
 * Keeping these out of the reducer lets the reducer stay thin (just
 * `updateSkills` + `setSelectedSkillPath`) and makes tree mutations
 * independently testable.
 */

type SkillsOpResult = {
  skills: SkillFileNode[];
  selectedPath?: string | null;
};

const SKILL_MD = 'SKILL.md';

const addFolderToRoot = (skills: SkillFileNode[]): SkillsOpResult => {
  const newFolder: SkillFileNode = {
    kind: 'folder',
    name: '',
    children: [{ kind: 'file', name: SKILL_MD, content: '' }],
  };
  const next = [...skills, newFolder];
  return { skills: next, selectedPath: `${next.length - 1}.0` };
};

const addFileToFolder = (skills: SkillFileNode[], parentPath: string): SkillsOpResult | null => {
  const folder = getAtPath(skills, parentPath);
  if (folder?.kind !== 'folder') return null;
  const newFile: SkillFileNode = { kind: 'file', name: '', content: '' };
  const next = appendToFolderAtPath(skills, parentPath, [newFile]);
  return { skills: next, selectedPath: `${parentPath}.${folder.children.length}` };
};

const removeNodeAt = (skills: SkillFileNode[], path: string, currentSelectedPath: string | null): SkillsOpResult => {
  const next = removeAtPath(skills, path);
  if (!currentSelectedPath || next === skills) return { skills: next, selectedPath: currentSelectedPath };
  const selectedCleared = currentSelectedPath === path || isDescendantPath(path, currentSelectedPath);
  // Removing a file or folder inside a skill leaves that skill open; only removing the skill itself closes it.
  const skillPath = path.includes('.') ? path.split('.')[0] : null;
  const selectedPath = selectedCleared ? skillPath : shiftPathAfterRemoval(currentSelectedPath, path);
  return { skills: next, selectedPath };
};

const renameNodeAt = (skills: SkillFileNode[], path: string, name: string): SkillsOpResult => ({
  skills: updateAtPath(skills, path, node => ({ ...node, name })),
});

const setFileContentAt = (skills: SkillFileNode[], path: string, content: string): SkillsOpResult => ({
  skills: updateAtPath(skills, path, node => (node.kind === 'file' ? { ...node, content } : node)),
});

const moveNodeTo = (
  skills: SkillFileNode[],
  fromPath: string,
  toPath: string,
  position: DropPosition,
): SkillsOpResult => {
  const next = moveNode(skills, fromPath, toPath, position);
  return { skills: next, selectedPath: next === skills ? undefined : null };
};

const shiftPathAfterInsertion = (
  selectedPath: string | null | undefined,
  targetPath: string,
  position: 'before' | 'after',
  count: number,
) => {
  if (!selectedPath) return selectedPath;
  const selected = selectedPath.split('.').map(Number);
  const target = targetPath.split('.').map(Number);
  const depth = target.length - 1;
  if (selected.length <= depth || target.slice(0, depth).some((index, level) => selected[level] !== index)) {
    return selectedPath;
  }
  const insertIndex = target[depth] + (position === 'after' ? 1 : 0);
  if (selected[depth] < insertIndex) return selectedPath;
  selected[depth] += count;
  return selected.join('.');
};

type DropExternalNodesArgs = {
  targetPath: string | null;
  position: DropPosition | 'root';
  nodes: SkillFileNode[];
  currentSelectedPath?: string | null;
};

/**
 * Drop nodes coming from outside the tree (OS drag-and-drop). Position
 * 'root' appends at the top level. Position 'inside' on a non-folder falls
 * back to inserting after.
 */
const dropExternalNodes = (
  skills: SkillFileNode[],
  { targetPath, position, nodes, currentSelectedPath }: DropExternalNodesArgs,
): SkillsOpResult => {
  if (nodes.length === 0) return { skills };
  if (position === 'root' || !targetPath) {
    return { skills: appendToRoot(skills, nodes), selectedPath: currentSelectedPath };
  }
  if (position === 'inside' && getAtPath(skills, targetPath)?.kind === 'folder') {
    return { skills: appendToFolderAtPath(skills, targetPath, nodes), selectedPath: currentSelectedPath };
  }
  const insertionPosition = position === 'inside' ? 'after' : position;
  const next = insertAtPath(skills, targetPath, insertionPosition, nodes);
  const selectedPath =
    next === skills
      ? currentSelectedPath
      : shiftPathAfterInsertion(currentSelectedPath, targetPath, insertionPosition, nodes.length);
  return { skills: next, selectedPath };
};

export type { SkillsOpResult };
export {
  addFileToFolder,
  addFolderToRoot,
  dropExternalNodes,
  moveNodeTo,
  removeNodeAt,
  renameNodeAt,
  setFileContentAt,
};
